import { useCallback, useEffect, useRef, useState } from 'react';
import { AutopilotCancelled } from 'shared/demo/autopilot';

export type AutopilotScript = (signal: AbortSignal, report: (label: string) => void) => Promise<void>;

// Executa um roteiro assíncrono cancelável. `label` descreve o que está sendo feito agora
// ("Digitando o nome…") para o status na tela; `stop` interrompe e devolve o controle.
export function useAutopilot() {
  const controllerRef = useRef<AbortController | null>(null);
  const [label, setLabel] = useState<string | null>(null);

  const stop = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
    setLabel(null);
  }, []);

  const run = useCallback(async (script: AutopilotScript) => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLabel('Preparando simulação…');
    try {
      await script(controller.signal, setLabel);
    } catch (error) {
      if (!(error instanceof AutopilotCancelled)) throw error;
    } finally {
      if (controllerRef.current === controller) {
        controllerRef.current = null;
        setLabel(null);
      }
    }
  }, []);

  // Sair da tela no meio da simulação cancela o roteiro.
  useEffect(() => () => controllerRef.current?.abort(), []);

  return { isRunning: label !== null, label, run, stop };
}
